import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGame } from '../contexts/GameContext';
import GamePlayUI from '../components/GamePlayUI';
import GameResults from '../components/GameResults';
import { CAT, Avatar, buildShareText, copyText, fmtTime } from '../components/ShareResults';
import './GameRoomPage.css';

// Errors that mean this page cannot show the room at all.
const LEAVE_CODES = ['NOT_FOUND', 'STARTED', 'FULL', 'REGISTERED_ONLY', 'NOT_IN_GAME'];

function Countdown({ count }) {
  return (
    <div className="room-count-wrap">
      <div className="room-count" key={count} aria-live="assertive">{count}</div>
    </div>
  );
}

function Lobby({ roomId, game, you, onReady, onLeave, colorOf }) {
  const me = game.players.find((p) => p.playerId === you);
  const isReady = me && me.status === 'ready';
  const starting = game.status === 'starting';
  const copy = () => copyText(roomId, 'Code copied');
  return (
    <div className="cr-page">
      <div className="room-lobby">
        <section className="cr-card cr-card-pad room-code-card">
          <div className="cr-label">Room code</div>
          <div className="room-code">{roomId}</div>
          <p className="cr-muted">Share this code with friends.</p>
          <div className="room-row">
            <button type="button" className="cr-btn sm" onClick={copy}>Copy Code</button>
            <span className={`cr-pill${game.isRated ? ' rated' : ''}`}>{game.isRated ? 'Rated' : 'Standard'}</span>
          </div>
          <div className="room-row room-actions">
            <button type="button" className={`cr-btn${isReady ? '' : ' solid'}`} onClick={() => onReady(!isReady)} disabled={!me}>
              {isReady ? 'Not Ready' : 'Ready'}
            </button>
            <button type="button" className="cr-btn" onClick={onLeave}>Leave Game</button>
          </div>
          <p className="cr-label">
            {starting ? 'Everyone is ready. Starting now.' : 'The game starts when every player is ready.'}
          </p>
        </section>

        <section className="cr-card room-players">
          <div className="cr-row-head cr-card-pad room-players-head">
            <h2 className="cr-section-title">Players</h2>
            <span className="cr-label cr-tnum">{game.players.length}/{game.maxPlayers || 4}</span>
          </div>
          <div>
            {[0, 1, 2, 3].map((i) => {
              const p = game.players[i];
              if (!p) {
                return (
                  <div key={`empty-${i}`} className="cr-tr room-empty">
                    <span className="cr-player"><span className="cr-av room-empty-av" aria-hidden="true" />Waiting for player</span>
                    <span />
                  </div>
                );
              }
              return (
                <div key={p.playerId} className={`cr-tr${p.playerId === you ? ' cr-me' : ''}`}>
                  <span className="cr-player">
                    <Avatar name={p.username} color={colorOf(p.playerId)} />
                    <b>{p.username}</b>
                    {p.rating != null && <span className="cr-faint cr-tnum">{p.rating}</span>}
                    {p.isHost && <span className="cr-pill">Host</span>}
                    {!p.connected && <span className="cr-faint">Offline</span>}
                  </span>
                  <span className={`room-ready${p.status === 'ready' ? ' yes' : ''}`}>
                    {p.status === 'ready' ? 'Ready' : 'Not Ready'}
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}

const GameRoom = ({ roomId }) => {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const { socket, authed, leaveGame, playAgain, setCurrentGame, clearCurrentGame } = useGame();

  const [game, setGame] = useState(null);
  const [you, setYou] = useState(null);
  const [words, setWords] = useState([]);
  const [startedAt, setStartedAt] = useState(null);   // local clock ms
  const [myGroups, setMyGroups] = useState([]);
  const [guesses, setGuesses] = useState([]);
  const [lives, setLives] = useState(4);
  const [countdown, setCountdown] = useState(null);
  const [results, setResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [outAt, setOutAt] = useState(null);           // local ms when I ran out of lives
  const pending = useRef(new Map());
  const seqRef = useRef(0);
  const offsetRef = useRef(0); // server clock minus local clock

  // Apply a full state payload (room_created, room_joined, game_state).
  const applyState = useCallback((data) => {
    if (!data || data.roomId !== roomId) return;
    if (typeof data.serverNow === 'number') offsetRef.current = data.serverNow - Date.now();
    setGame(data.game);
    setYou(data.you);
    setWords(data.puzzle ? data.puzzle.words : []);
    setStartedAt(data.game.startedAt ? new Date(data.game.startedAt).getTime() - offsetRef.current : null);
    if (data.me) {
      setMyGroups(data.me.foundGroups || []);
      setGuesses(data.me.guesses || []);
      setLives(data.me.lives);
      setOutAt(data.me.outAt ? new Date(data.me.outAt).getTime() - offsetRef.current : null);
    }
    if (data.game.status !== 'starting') setCountdown(null);
    setResults(data.results || null);
    setShowResults(!!data.results);
    setCurrentGame(data.game.status === 'completed' ? null : { roomId, status: data.game.status });
  }, [roomId, setCurrentGame]);

  const patchPlayer = useCallback((playerId, patch) => {
    setGame((g) => (g ? { ...g, players: g.players.map((p) => (p.playerId === playerId ? { ...p, ...patch } : p)) } : g));
  }, []);

  useEffect(() => {
    if (!socket || !authed) return undefined;
    const mine = (d) => d && (!d.roomId || d.roomId === roomId);
    const onGame = (d) => { if (d && d.game && d.game.roomId === roomId) setGame(d.game); };
    const handlers = {
      room_created: applyState,
      room_joined: applyState,
      game_state: applyState,
      game_update: onGame,
      player_joined: onGame,
      player_left: onGame,
      player_rejoined: onGame,
      game_countdown: (d) => setCountdown(d.count),
      countdown_cancelled: (d) => { if (mine(d)) setCountdown(null); },
      game_start: (d) => {
        if (!mine(d)) return;
        if (typeof d.serverNow === 'number') offsetRef.current = d.serverNow - Date.now();
        setCountdown(null);
        setGame(d.game);
        setWords(d.puzzle.words);
        setStartedAt(new Date(d.startedAt).getTime() - offsetRef.current);
        setMyGroups([]);
        setGuesses([]);
        setLives(4);
        setOutAt(null);
        setResults(null);
        setShowResults(false);
        setCurrentGame({ roomId, status: 'playing' });
      },
      player_progress: (d) => patchPlayer(d.playerId, { foundLevels: d.foundLevels, foundCount: d.foundCount }),
      player_lives: (d) => patchPlayer(d.playerId, { lives: d.lives }),
      player_out: (d) => patchPlayer(d.playerId, { status: 'out', ...(d.reason === 'forfeit' ? { forfeited: true } : {}) }),
      player_completed: (d) => patchPlayer(d.playerId, { status: 'completed', timeTaken: d.timeTaken }),
      game_completed: (d) => {
        if (!mine(d)) return;
        setResults(d);
        // Keep seat order (avatar colors); take the final numbers from the results.
        setGame((g) => (g ? {
          ...g,
          status: 'completed',
          players: g.players.map((p) => ({ ...p, ...(d.players.find((x) => x.playerId === p.playerId) || {}) })),
        } : g));
        clearCurrentGame();
        setTimeout(() => setShowResults(true), 1200);
      },
      connection_correct: (d) => {
        setMyGroups((gs) => (gs.some((g) => g.level === d.level) ? gs : [...gs, d]));
        const resolve = pending.current.get(d.seq);
        if (resolve) { pending.current.delete(d.seq); resolve({ type: 'correct', foundCount: d.foundCount }); }
      },
      connection_incorrect: (d) => {
        setLives(d.lives);
        if (d.lives === 0) setOutAt(Date.now());
        const resolve = pending.current.get(d.seq);
        if (resolve) { pending.current.delete(d.seq); resolve({ type: 'incorrect', oneAway: d.oneAway, lives: d.lives }); }
      },
      connection_rejected: (d) => {
        const resolve = pending.current.get(d.seq);
        if (resolve) { pending.current.delete(d.seq); resolve({ type: 'rejected', message: d.message }); }
      },
      game_error: (d) => {
        if (!d) return;
        // The context already shows the message. Unblock a waiting guess, and leave rooms we cannot be in.
        pending.current.forEach((resolve) => resolve({ type: 'error' }));
        pending.current.clear();
        if (d.code === 'ALREADY_IN_GAME' && d.roomId && d.roomId !== roomId) {
          navigateRef.current(`/game/${d.roomId}`, { replace: true });
        } else if ((d.roomId === roomId || (!d.roomId && d.code === 'NOT_FOUND')) && LEAVE_CODES.includes(d.code)) {
          clearCurrentGame();
          navigateRef.current('/', { replace: true });
        }
      },
    };
    Object.entries(handlers).forEach(([event, fn]) => socket.on(event, fn));
    // Join (or rejoin after a reload or reconnect). The server replies with the full state.
    socket.emit('join_room', { roomId });
    const pendingMap = pending.current;
    return () => {
      Object.entries(handlers).forEach(([event, fn]) => socket.off(event, fn));
      pendingMap.forEach((resolve) => resolve(null));
      pendingMap.clear();
    };
  }, [socket, authed, roomId, applyState, patchPlayer, setCurrentGame, clearCurrentGame]);

  const submitGuess = useCallback((selection) => new Promise((resolve) => {
    if (!socket) return resolve({ type: 'error', message: 'Not connected' });
    seqRef.current += 1;
    const seq = seqRef.current;
    pending.current.set(seq, (res) => {
      if (res && res.type !== 'rejected' && res.type !== 'error') setGuesses((g) => [...g, selection]);
      resolve(res);
    });
    socket.emit('submit_connection', { roomId, words: selection, seq });
    setTimeout(() => {
      const r = pending.current.get(seq);
      if (r) { pending.current.delete(seq); r({ type: 'error', message: 'No answer from the server. Try again.' }); }
    }, 8000);
  }), [socket, roomId]);

  const colorOf = useMemo(() => {
    const map = new Map();
    (game ? game.players : []).forEach((p, i) => map.set(p.playerId, CAT[i % 4]));
    return (id) => map.get(id) || CAT[0];
  }, [game]);

  const onLeave = async () => {
    await leaveGame(roomId);
    navigate('/');
  };
  const onReady = (ready) => {
    if (socket) socket.emit('toggle_ready', { roomId, ready });
  };

  if (!game) {
    return (
      <div className="cr-page">
        <p className="cr-muted room-loading">{authed ? 'Loading game' : 'Connecting to the game server'}</p>
      </div>
    );
  }

  const me = game.players.find((p) => p.playerId === you);
  const overlay = countdown != null ? <Countdown count={countdown} /> : null;

  if (game.status === 'waiting' || game.status === 'starting') {
    return (
      <>
        <Lobby roomId={roomId} game={game} you={you} onReady={onReady} onLeave={onLeave} colorOf={colorOf} />
        {overlay}
      </>
    );
  }

  if (results && showResults) {
    const mine = results.players.find((p) => p.playerId === you) || {};
    const solved = mine.status === 'completed';
    const history = (mine.submissions || []).map((s) => s.levels);
    const foundLevels = mine.foundLevels || [];
    const missed = results.solution.filter((g) => !foundLevels.includes(g.level));
    let rating;
    if (!results.isRated) rating = { value: '-', label: 'Unrated game' };
    else if (typeof mine.ratingChange === 'number') {
      rating = {
        value: `${mine.ratingChange >= 0 ? '+' : ''}${mine.ratingChange}`,
        label: `Rating, now ${mine.newRating}`,
        tone: mine.ratingChange >= 0 ? 'up' : 'down',
      };
    } else rating = { value: '-', label: 'Rating unchanged' };
    const share = buildShareText({
      title: 'Connections Racer',
      subtitle: `Room ${roomId}, place ${mine.place} of ${results.players.length}`,
      solved,
      time: mine.timeTaken,
      history,
    });
    return (
      <GameResults
        label={`Room ${roomId}`}
        title={solved ? 'Puzzle Solved!' : 'Out of Lives'}
        stats={[
          { label: 'Time', value: solved ? fmtTime(mine.timeTaken) : '-' },
          { label: 'Place', value: mine.place || '-', suffix: `/ ${results.players.length}` },
          { label: 'Mistakes', value: mine.mistakes ?? 0 },
          rating,
        ]}
        table={{
          title: 'Final Standings',
          kind: 'race',
          rows: results.players.map((p) => ({
            key: p.playerId,
            rank: p.place,
            name: p.username,
            color: colorOf(p.playerId),
            isYou: p.playerId === you,
            foundLevels: p.foundLevels,
            time: p.status === 'completed' ? p.timeTaken : null,
            lives: p.lives,
          })),
        }}
        history={history}
        missed={missed}
        onCopy={() => copyText(share, 'Results copied')}
        actions={(
          <>
            <button type="button" className="cr-btn solid" onClick={() => playAgain(roomId)}>Play Again</button>
            <button type="button" className="cr-btn" onClick={() => navigate('/')}>Back to Home</button>
          </>
        )}
      />
    );
  }

  // Playing (or the game just ended and results are about to show).
  const myStatus = me ? me.status : 'out';
  const done = myStatus === 'completed' || myStatus === 'out';
  const myEnd = me && me.status === 'completed' && startedAt != null && typeof me.timeTaken === 'number'
    ? startedAt + me.timeTaken * 1000
    : null;
  const endedAt = myEnd || (done ? (outAt || startedAt) : null);
  const players = game.players.map((p) => ({ ...p, isYou: p.playerId === you, color: colorOf(p.playerId) }));
  const waiting = game.players.filter((p) => p.status === 'playing').length;

  return (
    <>
      <GamePlayUI
        label={`Room ${roomId}`}
        words={words}
        foundGroups={myGroups}
        guesses={guesses}
        lives={me ? me.lives ?? lives : lives}
        startedAt={startedAt}
        endedAt={endedAt}
        locked={done || game.status !== 'playing'}
        onSubmit={submitGuess}
        players={players}
        footer={done ? (
          <div className="race-footer">
            <p>
              {game.status === 'completed' || waiting === 0
                ? 'Game over.'
                : `Waiting for ${waiting} ${waiting === 1 ? 'player' : 'players'} to finish.`}
            </p>
            <div className="race-footer-actions">
              {results && <button type="button" className="cr-btn solid" onClick={() => setShowResults(true)}>See Results</button>}
              <button type="button" className="cr-btn" onClick={() => navigate('/')}>Back to Home</button>
            </div>
          </div>
        ) : null}
      />
      {overlay}
    </>
  );
};

// Remount per room so nothing from a previous room (for example after Play Again) carries over.
const GameRoomPage = () => {
  const params = useParams();
  const roomId = String(params.roomId || '').toUpperCase();
  return <GameRoom key={roomId} roomId={roomId} />;
};

export default GameRoomPage;
